import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { Group, GroupMember, GroupMission, GroupActivity, MissionProgress, ActivityReaction, ActivityComment } from '@/types';
import { toast } from 'sonner';
import { normalizePillarName } from '@/lib/pillarMapping';

interface GroupState {
  // Estado
  myGroups: Group[];
  currentGroup: Group | null;
  groupActivities: GroupActivity[];
  isLoading: boolean;
  error: string | null;
  
  // Groups
  fetchMyGroups: () => Promise<void>;
  fetchGroupById: (groupId: string) => Promise<void>;
  createGroup: (data: { 
    nome: string; 
    descricao?: string; 
    regras?: string; 
    max_members?: number;
    pillar_focus?: string;
    activities?: any[];
    default_activity_duration?: number;
    tolerancia_faltas?: number;
    visibilidade_feed?: string;
    permitir_comentarios?: boolean;
    is_public?: boolean;
  }) => Promise<string>;
  updateGroup: (groupId: string, updates: Partial<Group>) => Promise<void>;
  deleteGroup: (groupId: string) => Promise<void>;
  
  // Members
  joinGroup: (groupId: string) => Promise<void>;
  leaveGroup: (groupId: string) => Promise<void>;
  updateMemberSettings: (groupId: string, settings: Partial<GroupMember>) => Promise<void>;
  
  // Missions
  createMission: (mission: { 
    group_id: string; 
    tipo: GroupMission['tipo']; 
    titulo: string; 
    descricao?: string;
    parametros?: Record<string, any>;
    start_date: string;
    end_date: string;
    progresso_total: number;
    recompensa_pontos?: number;
    recompensa_badge_id?: string;
  }) => Promise<void>;
  updateMissionProgress: (missionId: string, progress: { completed_days: number; contributed_today?: boolean }) => Promise<void>;
  
  // Activities
  fetchGroupActivities: (groupId: string, limit?: number) => Promise<void>;
  createActivity: (activity: { 
    group_id: string; 
    tipo: GroupActivity['tipo']; 
    payload: Record<string, any> 
  }) => Promise<void>;
  reactToActivity: (activityId: string, tipo: ActivityReaction['tipo']) => Promise<void>;
  commentOnActivity: (activityId: string, text: string) => Promise<void>;
  
  // Realtime
  subscribeToGroup: (groupId: string) => () => void;
  
  // Admin actions
  reactivateGroup: (groupId: string) => Promise<void>;
  permanentDeleteGroup: (groupId: string) => Promise<void>;
  
  // Reset
  reset: () => void;
}

export const useGroupStore = create<GroupState>((set, get) => ({
  myGroups: [],
  currentGroup: null,
  groupActivities: [],
  isLoading: false,
  error: null,
  
  // ============ GROUPS ============
  
  fetchMyGroups: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      // ✅ OTIMIZADO: Usar função RPC com uma única query otimizada
      const { data, error } = await supabase.rpc('get_user_groups', {
        p_user_id: user.id
      });
      
      if (error) throw error;
      
      const groupsFormatted = (data?.map((g: any) => ({
        id: g.id,
        nome: g.nome,
        descricao: g.descricao,
        regras: g.regras,
        is_public: g.is_public,
        max_members: g.max_members,
        pontos_grupo: g.pontos_grupo,
        nivel_grupo: g.nivel_grupo,
        pillar_focus: g.pillar_focus,
        activities: g.activities || [],
        default_activity_duration: g.default_activity_duration,
        owner_user_id: g.owner_user_id,
        created_at: g.created_at,
        updated_at: g.updated_at,
        tolerancia_faltas: g.tolerancia_faltas,
        visibilidade_feed: g.visibilidade_feed,
        permitir_comentarios: g.permitir_comentarios,
        member_count: Number(g.member_count) || 0,
        owner: g.owner_nome ? {
          id: g.owner_user_id,
          nome: g.owner_nome,
          avatar_url: g.owner_avatar_url
        } : undefined
      })) || []) as Group[];
      
      set({ myGroups: groupsFormatted, isLoading: false });
    } catch (error: any) {
      console.error('Error fetching groups:', error);
      set({ error: error.message, isLoading: false });
      toast.error('Erro ao carregar grupos');
    }
  },
  
  fetchGroupById: async (groupId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('groups')
        .select(`
          *,
          owner:profiles!owner_user_id(id, nome, avatar_url),
          members:group_members(*, profile:profiles(id, nome, avatar_url)),
          current_mission:group_missions!group_id(*, progress_by_member:mission_progress(*, profile:profiles(id, nome, avatar_url)))
        `)
        .eq('id', groupId)
        .single();
      
      if (error) throw error;
      const group = {
        ...data,
        activities: data.activities ? (data.activities as any) as GroupActivity[] : [],
        current_mission: data.current_mission?.[0] || null
      } as Group;
      set({ currentGroup: group, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      toast.error('Erro ao carregar grupo');
    }
  },
  
  createGroup: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      console.log('📝 Criando grupo:', { nome: data.nome, owner: user.id });
      
      const { data: newGroup, error: groupError } = await supabase
        .from('groups')
        .insert({
          nome: data.nome,
          descricao: data.descricao,
          regras: data.regras,
          owner_user_id: user.id,
          max_members: data.max_members || 10,
          pillar_focus: data.pillar_focus,
          is_public: data.is_public ?? false,
          tolerancia_faltas: data.tolerancia_faltas ?? 1,
          visibilidade_feed: data.visibilidade_feed ?? 'total',
          permitir_comentarios: data.permitir_comentarios ?? true,
          activities: data.activities ? JSON.parse(JSON.stringify(data.activities)) : null,
          default_activity_duration: data.default_activity_duration
        })
        .select()
        .single();
      
      if (groupError) {
        console.error('❌ Erro ao criar grupo:', groupError);
        throw groupError;
      }
      
      console.log('✅ Grupo criado, ID:', newGroup.id);
      
      // ✅ CRÍTICO: Verificar se owner já é membro (pode ter sido adicionado por trigger)
      const { data: existingMember } = await supabase
        .from('group_members')
        .select('id')
        .eq('group_id', newGroup.id)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (existingMember) {
        console.log('✅ Owner já é membro (trigger funcionou)');
      } else {
        console.log('⚠️ Owner não é membro, adicionando manualmente...');
        
        // Adicionar owner como membro
        const { error: memberError } = await supabase
          .from('group_members')
          .insert({
            group_id: newGroup.id,
            user_id: user.id,
            role: 'owner'
          });
        
        if (memberError) {
          console.error('❌ CRÍTICO: Falha ao adicionar owner como membro!', memberError);
          
          // Tentar deletar grupo órfão
          await supabase
            .from('groups')
            .delete()
            .eq('id', newGroup.id);
          
          throw new Error('Erro ao configurar permissões do grupo. Tente novamente.');
        }
        
        console.log('✅ Owner adicionado como membro manualmente');
      }
      
      await get().fetchMyGroups();
      set({ isLoading: false });
      toast.success('Grupo criado com sucesso!');
      return newGroup.id;
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      const errorMessage = error.message || 'Erro ao criar grupo';
      console.error('❌ Erro final ao criar grupo:', error);
      toast.error(errorMessage);
      throw error;
    }
  },
  
  updateGroup: async (groupId, updates) => {
    try {
      // Cast activities para JSON se existir
      const updatesForDB: any = {
        ...updates,
        activities: updates.activities ? JSON.parse(JSON.stringify(updates.activities)) : updates.activities
      };
      
      const { error } = await supabase
        .from('groups')
        .update(updatesForDB)
        .eq('id', groupId);
      
      if (error) throw error;
      
      await get().fetchMyGroups();
      if (get().currentGroup?.id === groupId) {
        await get().fetchGroupById(groupId);
      }
      toast.success('Grupo atualizado');
    } catch (error: any) {
      toast.error('Erro ao atualizar grupo');
    }
  },
  
  deleteGroup: async (groupId) => {
    try {
      const { error } = await supabase
        .from('groups')
        .delete()
        .eq('id', groupId);
      
      if (error) throw error;
      
      await get().fetchMyGroups();
      if (get().currentGroup?.id === groupId) {
        set({ currentGroup: null });
      }
      toast.success('Grupo deletado');
    } catch (error: any) {
      toast.error('Erro ao deletar grupo');
    }
  },
  
  // ============ MEMBERS ============
  
  joinGroup: async (groupId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      // 1. Buscar configuração do grupo antes de entrar
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .select('nome, pillar_focus, activities, default_activity_duration')
        .eq('id', groupId)
        .single();
      
      if (groupError) throw groupError;
      
      // 2. Entrar no grupo
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: groupId,
          user_id: user.id
        });
      
      if (memberError) throw memberError;
      
      // 3. Criar hábitos automaticamente para cada atividade do grupo
      let habitsCreated = 0;
      if (group.activities && Array.isArray(group.activities) && group.activities.length > 0) {
        for (const activity of group.activities as any[]) {
          try {
              // ✅ Normalizar nome do pilar antes de criar hábito
              const normalizedPillar = normalizePillarName(group.pillar_focus || 'saudavel');
              
              const { error: habitError } = await supabase
              .from('user_habits')
              .insert({
                user_id: user.id,
                title: `${group.nome} - ${activity.name}`,
                pilar: normalizedPillar,
                pillars: [normalizedPillar],
                frequency: 'diaria',
                days_of_week: ['0', '1', '2', '3', '4', '5', '6'], // Todos os dias
                active: true,
                dados_adicionais: {
                  group_id: groupId,
                  activity_name: activity.name,
                  min_duration: activity.duration_minutes,
                  auto_created: true
                }
              });
            
            if (!habitError) {
              habitsCreated++;
            }
          } catch (error) {
            console.error('Erro ao criar hábito:', error);
          }
        }
      }
      
      await get().fetchMyGroups();
      
      const successMsg = habitsCreated > 0 
        ? `Você entrou no grupo! ${habitsCreated} hábito(s) adicionado(s) aos seus hábitos diários.`
        : 'Você entrou no grupo!';
      
      toast.success(successMsg);
    } catch (error: any) {
      toast.error('Erro ao entrar no grupo');
    }
  },
  
  leaveGroup: async (groupId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      await get().fetchMyGroups();
      if (get().currentGroup?.id === groupId) {
        set({ currentGroup: null });
      }
      toast.success('Você saiu do grupo');
    } catch (error: any) {
      toast.error('Erro ao sair do grupo');
    }
  },
  
  updateMemberSettings: async (groupId, settings) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('group_members')
        .update(settings)
        .eq('group_id', groupId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      toast.success('Configurações atualizadas');
    } catch (error: any) {
      toast.error('Erro ao atualizar configurações');
    }
  },
  
  // ============ MISSIONS ============
  
  createMission: async (mission) => {
    try {
      const { error } = await supabase
        .from('group_missions')
        .insert(mission);
      
      if (error) throw error;
      
      if (get().currentGroup?.id === mission.group_id) {
        await get().fetchGroupById(mission.group_id);
      }
      toast.success('Missão criada!');
    } catch (error: any) {
      toast.error('Erro ao criar missão');
    }
  },
  
  updateMissionProgress: async (missionId, progress) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('mission_progress')
        .upsert({
          mission_id: missionId,
          user_id: user.id,
          ...progress,
          updated_at: new Date().toISOString()
        });
      
      if (error) throw error;
      toast.success('Progresso atualizado!');
    } catch (error: any) {
      toast.error('Erro ao atualizar progresso');
    }
  },
  
  // ============ ACTIVITIES ============
  
  fetchGroupActivities: async (groupId, limit = 50) => {
    try {
      const { data, error } = await supabase
        .from('group_activities')
        .select(`
          *,
          profile:profiles!user_id(id, nome, avatar_url),
          reactions:activity_reactions(id, tipo, user_id),
          comments:activity_comments(id, comment_text, user_id, created_at, profile:profiles(id, nome, avatar_url))
        `)
        .eq('group_id', groupId)
        .order('timestamp', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      
      // Calcular contagem de reações
      const activitiesWithCounts = (data?.map(activity => ({
        ...activity,
        reaction_count: (activity.reactions || []).reduce((acc: Record<string, number>, r: any) => {
          acc[r.tipo] = (acc[r.tipo] || 0) + 1;
          return acc;
        }, {})
      })) || []) as GroupActivity[];
      
      set({ groupActivities: activitiesWithCounts });
    } catch (error: any) {
      toast.error('Erro ao carregar atividades');
    }
  },
  
  createActivity: async (activity) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('group_activities')
        .insert({
          ...activity,
          user_id: user.id
        });
      
      if (error) throw error;
    } catch (error: any) {
      console.error('Error creating activity:', error);
    }
  },
  
  reactToActivity: async (activityId, tipo) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('activity_reactions')
        .insert({
          activity_id: activityId,
          user_id: user.id,
          tipo
        });
      
      if (error) {
        // Se já existe, remove a reação
        await supabase
          .from('activity_reactions')
          .delete()
          .eq('activity_id', activityId)
          .eq('user_id', user.id)
          .eq('tipo', tipo);
      }
      
      // Recarregar atividades
      const currentGroup = get().currentGroup;
      if (currentGroup) {
        await get().fetchGroupActivities(currentGroup.id);
      }
    } catch (error: any) {
      console.error('Error reacting:', error);
    }
  },
  
  commentOnActivity: async (activityId, text) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('activity_comments')
        .insert({
          activity_id: activityId,
          user_id: user.id,
          comment_text: text
        });
      
      if (error) throw error;
      
      // Recarregar atividades
      const currentGroup = get().currentGroup;
      if (currentGroup) {
        await get().fetchGroupActivities(currentGroup.id);
      }
      toast.success('Comentário adicionado');
    } catch (error: any) {
      toast.error('Erro ao comentar');
    }
  },
  
  // ============ REALTIME ============
  
  subscribeToGroup: (groupId: string) => {
    const channel = supabase
      .channel(`group:${groupId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'group_activities',
          filter: `group_id=eq.${groupId}`
        },
        (payload) => {
          get().fetchGroupActivities(groupId);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'mission_progress'
        },
        (payload) => {
          if (get().currentGroup?.current_mission) {
            get().fetchGroupById(groupId);
          }
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  },
  
  // ============================================
  // ADMIN ACTIONS - Arquivamento
  // ============================================
  
  reactivateGroup: async (groupId: string) => {
    try {
      const { error } = await supabase
        .from('groups')
        .update({ archived_at: null })
        .eq('id', groupId);
      
      if (error) throw error;
      
      toast.success('Grupo reativado com sucesso');
    } catch (error: any) {
      console.error('Erro ao reativar grupo:', error);
      toast.error('Erro ao reativar grupo');
      throw error;
    }
  },

  permanentDeleteGroup: async (groupId: string) => {
    try {
      const { error } = await supabase
        .from('groups')
        .delete()
        .eq('id', groupId);
      
      if (error) throw error;
      
      toast.success('Grupo excluído definitivamente');
    } catch (error: any) {
      console.error('Erro ao excluir grupo:', error);
      toast.error('Erro ao excluir grupo');
      throw error;
    }
  },

  reset: () => set({
    myGroups: [],
    currentGroup: null,
    groupActivities: [],
    isLoading: false,
    error: null
  })
}));